import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, inject, SimpleChanges, OnChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil, filter, distinctUntilChanged } from 'rxjs';
import { CommonModule, formatDate } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SharedModule } from '../../../../../../shared/shared.module';
import { WorkOrderLineView } from '../../../work-order-line-list/model/work-order-line-view.model';
import { RepairDTO, WorkOrderLineExtended } from '../../model/work-order-line-extended.model';
import { WorkOrder } from '../../../model/workorder';
import { TabComponent } from '../tab-data.interface';
import { MatTableDataSource } from '@angular/material/table';
import { AlertType, EmptyStateModule, StatusModule, TableConfig, TableModule } from '@michelin/theme';
import { TranslationService } from '../../../../../../shared/translations/services/translation.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../../../shared/state/TranslationState';
import { WorkOrderService } from '../../../../services/workorder.service';
import { CreateWorkOrderService } from '../../../createworkorder/create-worker-order.service';
import { Dealer } from '../../../../../../shared/dealer/models/dealer';
import { DealerState } from '../../../../../../shared/state/DealerState';
import { FEATURE_FLAGS } from '../../../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../../../shared/services/featureflags.service';
import {
  enableRequiredField,
  hasWorkOrderEditRoles,
  processCodeToWorkType,
  REQUIRED,
  WorkOrderLineEditViolation
} from '../../../../shared/util/work-order-helper-util';
import { ActivatedRoute, Router } from '@angular/router';
import { optionalBrandNumber, sanitizeBrandNumber } from '../../../../shared/util/work-order-validators';
import { environment } from '../../../../../../../environments/environment';
import { WorkOrderLineRequestModel } from '../../model/work-order-line-request.model';
import { UserState } from '../../../../../../shared/state/UserState';
import { UtilityService } from '../../../../../../shared/services/utility.service';
import { WorkOrderRequiredFieldFlags } from '../../../../../../shared/services/models/WorkOrderRequiredFieldFlags';
import { MichelinDropdownAccessibilityDirective } from '../../../../../../shared/directives/michelin-dropdown-accessibility.directive';

interface SaveCompleteEvent {
  action: string;
  success: boolean;
  response?: any;
}

@Component({
  selector: 'app-work-order-line-tab',
  templateUrl: './work-order-line-tab.component.html',
  styleUrls: ['./work-order-line-tab.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatCardModule,
    MatDividerModule,
    MatTooltipModule,
    SharedModule,
    TableModule,
    EmptyStateModule,
    StatusModule,
    MatProgressSpinnerModule,
    MichelinDropdownAccessibilityDirective
  ]
})
export class WorkOrderLineTabComponent implements OnInit, OnDestroy, OnChanges, TabComponent {
  readonly rtDotRejectRequiredErrorKey = 'rtDotRequiredForReject';

  get loadingExtendedDetails(): boolean {
    return !this.workOrderLineExtended;
  }
  get isAdCustomerType(): boolean {
    return this.customerType === 'AD';
  }

  private route = inject(ActivatedRoute);

  @Input() workOrderLine: WorkOrderLineView | null = null;
  @Input() workOrderLineExtended: WorkOrderLineExtended | null = null;
  @Input() workOrder: WorkOrder | null = null;
  @Input() showMfgDotField: boolean = false;
  @Input() showRtDotField: boolean = false;
  @Input() customerType: string | null = null;
  @Output() dataChanged = new EventEmitter<any>();
  @Output() hasChanges = new EventEmitter<boolean>();
  @Output() camCheckRequired = new EventEmitter<{ ntr: number; mfgDOT: string }>();
  @Output() saveComplete = new EventEmitter<SaveCompleteEvent>();
  public workOrderService = inject(WorkOrderService);
  public createWorkOrderService = inject(CreateWorkOrderService);
  public store = inject(Store);
  public utilityService = inject(UtilityService);
  public translationService = inject(TranslationService);
  public formBuilder = inject(FormBuilder);
  public featureflagsService = inject(FeatureflagsService);
  public readonly router = inject(Router);

  workOrderLineForm: FormGroup;
  repairsForm: FormGroup;
  private destroy$ = new Subject<void>();
  dealer: Dealer;
  workOrderEditable: boolean; // Assume this is set based on some logic or input
  compareByValue = (a, b) => a && b && (a.value == b || a.value === b.value);
  compareByCode = (a, b) => a && b && (a.code == b || a.code === b.code);
  codeCompareWith = (a: any, b: any): boolean => {
    const av = a && typeof a === 'object' ? a.value : a;
    const bv = b && typeof b === 'object' ? b.value : b;
    return av != null && bv != null && av === bv;
  };
  enableCasingValue = ['CC', 'CP', 'RS'];
  disableNTRValidation = ['RO', 'IO'];
  specialWorkType = ['AD', 'IO', 'NP', 'RO', 'RS', 'SC', 'SO'];
  treadEditDisableWorkType = ['IO', 'NP', 'RS', 'SC', 'SO'];
  readonly RR_EDITABLE_POSTS = ['INITIAL_INSPECTION', 'BUFFER', 'CIA', 'SKIVE', 'REPAIR', 'CEMENT', 'BUILDER'];
  readonly RO_EDITABLE_POSTS = ['INITIAL_INSPECTION'];
  isScrapCodeSelected: boolean = false;
  isNpCodeSelected: boolean = false;
  treadDescReadOnly: boolean = true;
  casingDetailsEditable: boolean = false;
  workOrderRequiredFieldFlag: WorkOrderRequiredFieldFlags;

  tableConfig: TableConfig<RepairDTO>;

  repairsDataSource = new MatTableDataSource<RepairDTO>([]);

  //Dropdown options
  prodCustomerOptionsDropdown: string[];
  prodCustomerOptions: { id: string; value: string }[] = [];
  overrideGradeOptions: { id: string; value: string }[] = [];
  processCodeOptions: { value: string }[] = [];
  makeOptions: { id: string; value: string }[] = [];
  seriesOptions: { id: string; value: string }[] = [];
  sizeOptions: { id: string; value: string }[] = [];
  treadDescOptions: { id: string; value: string; m3Flag: string }[] = [];
  wheelTypeOptions: { id: string; value: string }[] = [];
  wheelColorOptions: { id: string; value: string }[] = [];
  rejectReasonsOptions: { id: string; code: string; value: string }[] = [];
  treadLengthOptions: { id: string; value: string }[] = [];
  applicationOptions: { id: string; value: string }[] = [];
  axleOptions: { id: string; value: string }[] = [];

  constructor() {
    this.workOrderLineForm = this.formBuilder.group({
      casingId: ['', { validators: [Validators.required, Validators.maxLength(10)] }],
      prodCustomer: [''],
      processCode: ['', Validators.required],
      casingStatus: [''],
      workType: [''],
      make: ['', Validators.required],
      series: ['', Validators.required],
      size: ['', Validators.required],
      treadDesc: [''],
      camSpecID: [''],
      application: [''],
      axle: [''],
      ntr: ['', { validators: [Validators.maxLength(1), Validators.pattern('^[0-9]$')] }],
      mfgDOT: [''],
      rtDOT: [''],
      grade: [''],
      overrideGrade: [''],
      brand: ['', { validators: [Validators.maxLength(25), optionalBrandNumber()] }],
      sequence: [''],
      wheelType: [''],
      wheelColor: [''],
      casingNotes: [''],
      warrantyAdjustment: [''],
      casingValue: ['', { validators: [Validators.maxLength(9), Validators.pattern(/^\d{1,9}(\.\d+)?$/)], updateOn: 'blur' }],
      scrapCode: [''],
      npCode: [''],
      damageLength: [''],
      moldType: [''],
      width: [''],
      length: [''],
      disposalCharge: [''],
      minTreadDepth: [''],
      maxTreadDepth: [''],
      casingCreationDt: [''],
      casingReceiveDt: [''],
      casingCompleteDt: ['']
    });

    // Sanitize NTR on blur: keep only a single digit (remove alphabets and symbols)
    const ntrCtrl = this.workOrderLineForm.get('ntr');
    ntrCtrl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((raw) => {
      if (raw === null || raw === undefined) return;
      const str = String(raw);
      // Extract first digit only
      const cleaned = str.match(/\d/)?.[0] || '';
      if (cleaned !== str) {
        ntrCtrl.setValue(cleaned, { emitEvent: false });
        if (cleaned) ntrCtrl.markAsDirty();
      }
    });

    // Subscribe to casingValue blur updates to format to two decimals and remove alphabets
    const casingValueCtrl = this.workOrderLineForm.get('casingValue');
    casingValueCtrl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((raw) => {
      if (raw === null || raw === undefined) return;
      let original = String(raw);
      let value = original.trim();

      // Remove alphabetic characters and apply immediately
      const cleaned = value.replace(/[A-Za-z]/g, '');
      if (cleaned !== value) {
        value = cleaned;
        casingValueCtrl.setValue(value, { emitEvent: false });
        if (value) casingValueCtrl.markAsDirty();
      }

      if (!value) return;

      this.formatNumber(casingValueCtrl);
    });

    const brandCtrl = this.workOrderLineForm.get('brand');
    brandCtrl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((raw) => {
      if (raw === null || raw === undefined) return;
      const value = sanitizeBrandNumber(raw);
      // Only adjust when field is enabled (editable)
      if (brandCtrl?.disabled) return;
      const upper = value.toUpperCase().slice(0, 25);
      if (upper !== String(raw)) {
        brandCtrl.setValue(upper, { emitEvent: false });
        brandCtrl.markAsDirty();
      }
    });

    const rtDotCtrl = this.workOrderLineForm.get('rtDOT');
    rtDotCtrl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((raw) => {
      const value = String(raw ?? '').trim();
      if (value) {
        this.clearControlError(rtDotCtrl, this.rtDotRejectRequiredErrorKey);
      }
    });

    this.repairsForm = this.formBuilder.group({
      repairDetails: [''],
      repairDate: [''],
      repairTechnician: [''],
      repairComments: ['']
    });

    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.route.queryParams.subscribe((params) => {
      const isEditable = params['isEditable'] === 'true';
      this.workOrderEditable =
        this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.WORK_ORDER_EDIT_ENABLED) && hasWorkOrderEditRoles() && isEditable;
    });
  }

  ngOnInit(): void {
    this.buildTableConfig();
    this.store
      .select(TranslationState.currentLanguageTranslations)
      .pipe(
        filter((t) => !!t),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.buildTableConfig());
    if (this.workOrderLine || this.workOrderLineExtended) {
      this.populateFormData();
      this.setRepairsDataSource();
    }
    this.loadTreadDepthInfo();
    this.setupFormValueChanges();
    if (this.workOrderEditable) {
      this.getWorkOrderLineEditableDropdown();
      this.setRequiredBasedOnFlag();
    }
    this.setupProcessCodeSubscription();
    this.setupNtrMfgDotSubscriptions();
    this.applyProdCustomerValidator();
    this.setupTreadDescSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workOrderLine'] && this.workOrderLine) {
      this.populateFormData();
      this.setRepairsDataSource();
    }
    if (changes['workOrderLineExtended'] && this.workOrderLineExtended) {
      this.populateFormData();
      this.setRepairsDataSource();
    }
    if (changes['customerType'] || changes['workOrder']) {
      this.applyProdCustomerValidator();
    }
    if (changes['showMfgDotField']) {
      const mfgDotControl = this.workOrderLineForm.get('mfgDOT');
      if (mfgDotControl) {
        if (this.showMfgDotField) {
          mfgDotControl.setValidators([Validators.required]);
        } else {
          mfgDotControl.clearValidators();
        }
        mfgDotControl.updateValueAndValidity();
      }
    }

    if (changes['showRtDotField'] && !this.showRtDotField) {
      this.clearControlError(this.workOrderLineForm.get('rtDOT'), this.rtDotRejectRequiredErrorKey);
    }
  }

  private clearControlError(control: AbstractControl | null, errorKey: string): void {
    if (!control?.errors?.[errorKey]) {
      return;
    }

    const { [errorKey]: removedError, ...remainingErrors } = control.errors;
    void removedError;
    control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  private applyProdCustomerValidator(): void {
    const prodCustomerControl = this.workOrderLineForm.get('prodCustomer');
    if (this.isAdCustomerType) {
      prodCustomerControl?.setValidators([Validators.required]);
    } else {
      prodCustomerControl?.clearValidators();
    }
    prodCustomerControl?.updateValueAndValidity({ emitEvent: false });
  }

  private setupProcessCodeSubscription(): void {
    this.workOrderLineForm
      .get('processCode')
      ?.valueChanges.pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev?.value || prev) === JSON.stringify(curr?.value || curr)),
        takeUntil(this.destroy$)
      )
      .subscribe((processCode) => {
        const workType = processCodeToWorkType(processCode?.value || processCode);
        this.isScrapCodeSelected = workType === 'SC';
        if (this.isScrapCodeSelected) {
          enableRequiredField(this.workOrderLineForm.get('scrapCode'));
        }
        this.isNpCodeSelected = workType === 'NP';
        if (this.isNpCodeSelected) {
          enableRequiredField(this.workOrderLineForm.get('npCode'));
        }

        if (this.workOrderRequiredFieldFlag?.ntrRequired === REQUIRED && !this.disableNTRValidation.includes(workType)) {
          this.workOrderLineForm.get('ntr').setValidators([Validators.required, Validators.maxLength(1), Validators.pattern('^[0-9]$')]);
          this.workOrderLineForm.get('ntr').updateValueAndValidity({ emitEvent: false });
        } else {
          this.workOrderLineForm.get('ntr').clearValidators();
          this.workOrderLineForm.get('ntr').updateValueAndValidity({ emitEvent: false });
        }

        this.workOrderLineForm.get('workType')?.setValue(workType);

        // Call calculateCasingGrade API when process code changes
        this.recalculateCasingGrade(processCode);
      });
  }

  /**
   * Calls calculateCasingGrade when:
   * - processCode is one of the supported codes
   * - we have the required identifiers
   * - the form is not pristine (skips initial load/patchValue)
   */
  private recalculateCasingGrade(processCode: any): void {
    const processCodesToCall = ['CC', 'CP', 'BS', 'DS', 'RS'];
    const selectedProcessCode = processCode?.value || processCode;

    if (
      this.workOrder &&
      this.workOrderLine &&
      processCode &&
      selectedProcessCode &&
      processCodesToCall.includes(selectedProcessCode) &&
      !this.workOrderLineForm.pristine
    ) {
      const dto = {
        processCode: selectedProcessCode,
        mfgDOT: this.workOrderLineForm.get('mfgDOT')?.value,
        casingStatus: this.workOrderLineForm.get('casingStatus')?.value,
        ntr: this.workOrderLineForm.get('ntr')?.value,
        prodCustomer: this.getId(this.workOrderLineForm.get('prodCustomer')?.value, this.prodCustomerOptions),
        make: this.getId(this.workOrderLineForm.get('make')?.value, this.makeOptions),
        series: this.getId(this.workOrderLineForm.get('series')?.value, this.seriesOptions),
        size: this.getId(this.workOrderLineForm.get('size')?.value, this.sizeOptions),
        systemGradeFk: this.workOrderLineForm.get('grade')?.value
      };

      this.workOrderService
        .calculateCasingGrade(this.dealer.id, this.workOrder.id, this.workOrderLine.id, dto)
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          // Set only the part before the pipe symbol as the grade
          if (response?.data) {
            const grade = response.data.split('|')[0].trim();
            this.workOrderLineForm.get('grade')?.setValue(grade);
          }
        });
    } else {
      this.workOrderLineForm.get('grade')?.setValue(null);
    }
  }

  private setupNtrMfgDotSubscriptions(): void {
    // Recalculate grade when the user changes NTR or MfgDOT (skip initial patchValue via pristine check)
    const processCodeCtrl = this.workOrderLineForm.get('processCode');

    this.workOrderLineForm
      .get('ntr')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.recalculateCasingGrade(processCodeCtrl?.value));

    this.workOrderLineForm
      .get('mfgDOT')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        const mfgDOTValue = this.workOrderLineForm.get('mfgDOT')?.value;
        if ((mfgDOTValue && mfgDOTValue.length >= 4) || mfgDOTValue === '') {
          this.recalculateCasingGrade(processCodeCtrl?.value);
        }
      });
  }

  private setupTreadDescSubscription(): void {
    this.workOrderLineForm
      .get('treadDesc')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((tread) => {
        if (tread && tread.application) {
          this.workOrderLineForm.get('application')?.setValue(tread.application);
        }
        if (tread && tread.axle) {
          this.workOrderLineForm.get('axle')?.setValue(tread.axle);
        }
      });
  }

  codeAttribute = (item: any): string => {
    if (item === null || item === undefined) return '';
    return typeof item === 'string' ? item : item.value ?? '';
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildTableConfig(): void {
    this.tableConfig = {
      columns: {
        type: { cell: (elm: RepairDTO) => elm.code ?? '', header: this.translationService.translate('typeColumnText') },
        area: { cell: (elm: RepairDTO) => elm.area ?? '', header: this.translationService.translate('areaText') },
        quantity: { cell: (elm: RepairDTO) => elm.quantity?.toString() ?? '', header: this.translationService.translate('quantityText') },
        partNumber: { cell: (elm: RepairDTO) => elm.partNumber ?? '', header: this.translationService.translate('partNumberText') },
        status: {
          cell: 'statusCell',
          header: this.translationService.translate('statusText') || 'Status'
        }
      }
    };
  }

  private populateFormData(): void {
    if (this.workOrderLine) {
      this.workOrderLineForm.patchValue({
        casingId: this.workOrderLine.casingId,
        prodCustomer: this.workOrderLine.prodCustomer,
        processCode: this.workOrderLine.processCode,
        workType: this.workOrderLine.workType,
        make: this.workOrderLine.make,
        series: this.workOrderLine.series,
        size: this.workOrderLine.size,
        treadDesc: this.workOrderLine.treadDesc,
        mfgDOT: this.workOrderLine.mdot,
        brand: this.workOrderLine.brand ? this.workOrderLine.brand.toUpperCase().slice(0, 25) : this.workOrderLine.brand
      });
    }

    if (this.workOrderLineExtended) {
      this.workOrderLineForm.patchValue({
        camSpecID: this.workOrderLineExtended.camSpecName,
        application: this.workOrderLineExtended.applicationCode || this.workOrderLineExtended.applicationDesc,
        axle: this.workOrderLineExtended.axleCode || this.workOrderLineExtended.axleDesc,
        ntr: this.workOrderLineExtended.ntr,
        rtDOT: this.workOrderLineExtended.rtDot,
        grade: this.workOrderLineExtended.systemGradeCode,
        overrideGrade: this.workOrderLineExtended.overrideGradeCode,
        wheelType: this.workOrderLineExtended.wheelTypeCode,
        wheelColor: this.workOrderLineExtended.wheelColorDesc || this.workOrderLineExtended.wheelColorCode,
        casingNotes: this.workOrderLineExtended.tnote,
        casingValue: this.workOrderLineExtended.casingValue,
        scrapCode: this.workOrderLineExtended.scrapCode,
        npCode: this.workOrderLineExtended.npCode,
        damageLength: this.workOrderLineExtended.damageLength?.toString(),
        moldType: this.workOrderLineExtended.moldType,
        width: this.workOrderLineExtended.width || '',
        length: this.workOrderLineExtended.length ?? '',
        casingCreationDt: this.formatTimestamp(this.workOrderLineExtended.casingCreationDt),
        casingReceiveDt: this.formatTimestamp(this.workOrderLineExtended.casingReceiveDt),
        casingCompleteDt: this.formatTimestamp(this.workOrderLineExtended.casingCompleteDt)
      });
    }

    this.casingDetailsEditable = this.isCasingDetailsPostEditable();
    this.treadDescReadOnly = this.computeTreadDescReadOnly(this.workOrderLineForm.get('workType')?.value);
  }

  private formatTimestamp(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') return '';
    const date = new Date(/^\d+$/.test(String(value)) ? Number(value) : value);
    return isNaN(date.getTime()) ? '' : formatDate(date, 'yyyy-MM-dd HH:mm:ss', 'en-US');
  }

  // Shared by populateFormData() (synchronous first-paint value) and the valueChanges subscriber (live updates).
  private computeTreadDescReadOnly(workType: string): boolean {
    if (this.treadEditDisableWorkType.includes(workType)) {
      return true;
    }
    return !this.casingDetailsEditable;
  }

  // Shared by the valueChanges subscriber and the REJECT success handler to keep moldType's disabled state in sync.
  private shouldLockMoldType(workType: string): boolean {
    return this.treadEditDisableWorkType.includes(workType) || (workType === 'RO' && !this.casingDetailsEditable);
  }

  // Only further restricts workOrderEditable per work type/post; never loosens it.
  private isCasingDetailsPostEditable(): boolean {
    if (!this.workOrderEditable) return false;

    const workType = this.workOrderLine?.workType;
    if (workType !== 'RR' && workType !== 'RO') return this.workOrderEditable;

    if (this.workOrderLine?.casingStatus === 'EDITABLE' && this.workOrderLine?.post == null) {
      return true;
    }

    const normalizedPost = (this.workOrderLine?.post || '').trim().toUpperCase().replace(/\s+/g, '_');
    console.log('Normalized Post:', normalizedPost === '');
    if (workType === 'RR') return this.RR_EDITABLE_POSTS.includes(normalizedPost) || normalizedPost === '';
    return this.RO_EDITABLE_POSTS.includes(normalizedPost) || normalizedPost === '';
  }

  /**
   * Loads Tread Depth (Min/Max) for display in the Usage and Application section.
   * Uses the same location-info API as the Location Information tab, since these
   * shop-floor values are not part of the work order line extended payload.
   */
  private loadTreadDepthInfo(): void {
    if (!this.dealer?.id || !this.workOrderLine?.id || !this.workOrder?.id) {
      return;
    }
    this.workOrderService
      .fetchWorkOrderLineLocationInfo(this.dealer.id, this.workOrderLine.id, this.workOrder.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((locationInfo) => {
        const data = locationInfo?.data;
        if (!data) {
          return;
        }
        this.workOrderLineForm.patchValue(
          {
            minTreadDepth: data.treadDepthMin ?? '',
            maxTreadDepth: data.treadDepthMax ?? '',
            width: data.width ?? ''
          },
          { emitEvent: false }
        );
      });
  }

  private setupFormValueChanges(): void {
    this.workOrderLineForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.emitDataChanged();
      this.hasChanges.emit(this.workOrderLineForm.dirty || this.repairsForm.dirty);

      const processCode = this.workOrderLineForm.get('processCode')?.value?.value || this.workOrderLineForm.get('processCode')?.value;
      const workType = this.workOrderLineForm.get('workType')?.value;
      const moldTypeControl = this.workOrderLineForm.get('moldType');
      // enable/disable fields based on work type
      if (this.shouldLockMoldType(workType)) {
        moldTypeControl?.disable({ emitEvent: false });
      } else {
        moldTypeControl?.enable({ emitEvent: false });
      }
      this.treadDescReadOnly = this.computeTreadDescReadOnly(workType);
      this.onChangeTreadDescRequired(workType);
      if (this.workOrderLine?.casingStatus === 'SHOP_FLOOR') {
        this.workOrderLineForm.get('processCode')?.disable({ emitEvent: false });
      }
      if (this.enableCasingValue.includes(processCode) || processCode === undefined || environment.envName.includes('sa')) {
        enableRequiredField(this.workOrderLineForm.get('casingValue'));
      } else {
        this.workOrderLineForm.get('casingValue')?.disable({ emitEvent: false });
        this.workOrderLineForm.get('casingValue')?.setValue(null, { emitEvent: false });
      }
    });

    this.repairsForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.emitDataChanged();
      this.hasChanges.emit(this.workOrderLineForm.dirty || this.repairsForm.dirty);
    });

    this.workOrderLineForm
      .get('make')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((make) => {
        if (make?.id != undefined) {
          this.workOrderService.refreshSeriesDropdown(this.dealer.id, make.id).subscribe((series: any) => {
            this.seriesOptions = (series || []).map((item: any) => ({
              id: item.casingSeriesPk,
              value: item.casingSeries
            }));
            this.workOrderLineForm.get('series').setValue(null, { emitEvent: false });
          });
          this.refreshTreadsDescDropdown(make, null, this.workOrderLineForm.get('size').value);
          // Clear casingValue on make change
          this.workOrderLineForm.get('casingValue')?.setValue(null, { emitEvent: false });
        }
      });

    this.workOrderLineForm
      .get('series')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((series) => {
        if (series?.id != undefined) {
          const selectedMake = this.workOrderLineForm.get('make')?.value;
          const makeId =
            typeof selectedMake === 'object' ? selectedMake?.id : this.makeOptions.find((opt) => opt.value === selectedMake)?.id;
          this.workOrderService.refreshSizesDropdown(this.dealer.id, makeId, series.id).subscribe((sizes: any) => {
            this.sizeOptions = (sizes || []).map((item: any) => ({
              id: item.id,
              value: item.casingSize
            }));

            // Reselect the size if it exists in the new options
            const currentSize =
              this.workOrderLineForm.get('size')?.value?.value != undefined
                ? this.workOrderLineForm.get('size')?.value?.value
                : this.workOrderLineForm.get('size')?.value;
            const matchedSize = this.sizeOptions.find((opt) => {
              return opt.value == currentSize;
            });
            if (matchedSize) {
              this.workOrderLineForm.get('size')?.setValue(matchedSize, { emitEvent: false });
            } else {
              this.workOrderLineForm.get('size')?.setValue(null, { emitEvent: false });
            }
          });
          if (this.workOrderLineForm.get('moldType').value === 'M') {
            this.onMoldTypeChange();
          }

          // Clear casingValue on series change
          this.workOrderLineForm.get('casingValue')?.setValue(null, { emitEvent: false });
        }
      });

    this.workOrderLineForm
      .get('size')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((size) => {
        if (size?.id != undefined) {
          const currentMake = this.workOrderLineForm.get('make').value;
          const currentSeries = this.workOrderLineForm.get('series').value;
          this.refreshTreadsDescDropdown(currentMake, currentSeries, size);
          this.updateCasingValueFromSelection();
        } else {
          this.workOrderLineForm.get('casingValue')?.setValue('', { emitEvent: false });
        }
      });

    this.workOrderLineForm
      .get('treadDesc')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((treadDesc) => {
        if (treadDesc?.id != undefined) {
          // Clear casingValue on treadDesc change
          this.workOrderLineForm.get('casingValue')?.setValue(null, { emitEvent: false });
        }
      });
  }

  private updateCasingValueFromSelection(): void {
    const processCodeValue = this.workOrderLineForm.get('processCode')?.value;
    const selectedProcessCode = (processCodeValue?.value || processCodeValue || '').toString().toUpperCase();

    const customerUuid = this.getId(this.workOrderLineForm.get('prodCustomer')?.value, this.prodCustomerOptions);
    const casingSizeUuid = this.getId(this.workOrderLineForm.get('size')?.value, this.sizeOptions);

    if (!selectedProcessCode || !customerUuid || !casingSizeUuid) {
      this.workOrderLineForm.patchValue({ casingValue: '' }, { emitEvent: false });
      return;
    }

    this.workOrderService.getCasingValue(this.dealer.id, selectedProcessCode, customerUuid, casingSizeUuid).subscribe({
      next: (res) => {
        const raw = res?.casingValue ?? '';
        const formatted = this.applyFormatNumber(String(raw));
        this.workOrderLineForm.patchValue({ casingValue: formatted }, { emitEvent: false });
      },
      error: () => this.workOrderLineForm.patchValue({ casingValue: '' }, { emitEvent: false })
    });
  }

  private applyFormatNumber(nStr: string): string {
    if (nStr === '') return '0.00';
    const x = nStr.split('.');
    if (x.length > 1) {
      const x2 = '.' + x[1].substr(0, 2).padEnd(2, '0');
      return x[0] + x2;
    }
    if (x[0].length > 6) {
      return this.applyFormatNumber(x[0].substr(0, 6) + '.' + x[0].substr(3, x[0].length - 6));
    }
    return x[0] + '.00';
  }

  saveChanges(
    ignoreAgeCamValidation: boolean,
    ignoreNtrCamValidation: boolean,
    camViolationField: string,
    disposition: string,
    action: string
  ): void {
    if (action !== 'REJECT') {
      this.clearControlError(this.workOrderLineForm.get('rtDOT'), this.rtDotRejectRequiredErrorKey);
    }

    if (this.workOrderLineForm.invalid && action !== 'REJECT') {
      this.workOrderLineForm.markAllAsTouched();
      return;
    }

    let payload: WorkOrderLineRequestModel = {
      workOrderLineId: this.workOrderLine?.id || '',
      casingId: this.workOrderLineForm.get('casingId')?.value || '',
      prodCustomerName: this.workOrderLineForm.get('prodCustomer').value?.value || this.workOrderLineForm.get('prodCustomer')?.value,
      prodCustomerId:
        this.prodCustomerOptions.length > 0
          ? this.getId(this.workOrderLineForm.get('prodCustomer')?.value, this.prodCustomerOptions)
          : this.workOrderLine.prodCustomerId,
      processCode: this.workOrderLineForm.get('processCode').value?.value || this.workOrderLineForm.get('processCode')?.value,
      workType: this.workOrderLineForm.get('workType')?.value || '',
      casingStatus: this.workOrderLine?.casingStatus || '',
      shippingLocation: this.workOrderLine?.shippingLocation || '',
      postMarker: this.workOrderLine?.post || '',
      casingMakeFk:
        this.getIdFromDescription(this.workOrderLineForm.get('make')?.value?.value || this.workOrderLineForm.get('make')?.value, 'Make') ||
        '',
      casingSeriesFk:
        this.getIdFromDescription(
          this.workOrderLineForm.get('series')?.value?.value || this.workOrderLineForm.get('series')?.value,
          'Series'
        ) || '',
      casingSizeFk:
        this.getIdFromDescription(this.workOrderLineForm.get('size')?.value?.value || this.workOrderLineForm.get('size')?.value, 'Size') ||
        '',
      casingTreadDescFk:
        this.getIdFromDescription(
          this.workOrderLineForm.get('treadDesc')?.value?.value || this.workOrderLineForm.get('treadDesc')?.value,
          'TreadDesc'
        ) || '',
      casingTreadLengthFk:
        this.getIdFromDescription(
          this.workOrderLineForm.get('length')?.value?.value || this.workOrderLineForm.get('length')?.value,
          'TreadLength'
        ) || '',
      ntr: this.workOrderLineForm.get('ntr')?.value || '',
      mfgDot: this.workOrderLineForm.get('mfgDOT')?.value || '',
      rtDot: this.workOrderLineForm.get('rtDOT')?.value || '',
      brandNum: this.workOrderLineForm.get('brand')?.value || '',
      grade: this.workOrderLineForm.get('grade')?.value || '',
      overrideGrade:
        this.getIdFromDescription(
          this.workOrderLineForm.get('overrideGrade')?.value?.value || this.workOrderLineForm.get('overrideGrade')?.value,
          'OverrideGrade'
        ) || '',
      tireApplication: this.workOrderLineForm.get('application')?.value?.value || this.workOrderLineForm.get('application')?.value || '',
      tireAxle: this.workOrderLineForm.get('axle')?.value?.value || this.workOrderLineForm.get('axle')?.value || '',
      wheelTypeFk:
        this.getIdFromDescription(
          this.workOrderLineForm.get('wheelType')?.value?.value || this.workOrderLineForm.get('wheelType')?.value,
          'WheelType'
        ) || '',
      wheelColorFk:
        this.getIdFromDescription(
          this.workOrderLineForm.get('wheelColor')?.value?.value || this.workOrderLineForm.get('wheelColor')?.value,
          'WheelColor'
        ) || '',
      casingNotes: this.workOrderLineForm.get('casingNotes')?.value || '',
      casingValue: this.workOrderLineForm.get('casingValue')?.value || '',
      scrapCodeId:
        this.getIdFromDescription(
          this.workOrderLineForm.get('scrapCode')?.value?.value || this.workOrderLineForm.get('scrapCode')?.value,
          'RejectCode'
        ) || '',
      npCodeId:
        this.getIdFromDescription(
          this.workOrderLineForm.get('npCode')?.value?.value || this.workOrderLineForm.get('npCode')?.value,
          'RejectCode'
        ) || '',
      moldType: this.workOrderLineForm.get('moldType')?.value || '',
      updateBy: this.store.selectSnapshot(UserState.getUserName) || '',
      ignoredAgeViolation: ignoreAgeCamValidation,
      ignoredNtrViolation: ignoreNtrCamValidation,
      camViolationField: camViolationField,
      disposition: disposition,
      action: action
    };

    this.workOrderService.saveWorkOrderLineChanges(this.dealer.id, this.workOrder.id, this.workOrderLine.id, payload).subscribe({
      next: (response) => {
        if (response && response.data.success) {
          if (action === 'REJECT') {
            this.workOrderEditable = false;
            this.casingDetailsEditable = this.isCasingDetailsPostEditable();
            const workType = this.workOrderLineForm.get('workType')?.value;
            this.treadDescReadOnly = this.computeTreadDescReadOnly(workType);
            const moldTypeControl = this.workOrderLineForm.get('moldType');
            if (this.shouldLockMoldType(workType)) {
              moldTypeControl?.disable({ emitEvent: false });
            } else {
              moldTypeControl?.enable({ emitEvent: false });
            }
          } else if (action === 'DOWNGRADE') {
            this.utilityService.showAlert(AlertType.success, 'Success', this.translationService.translate('casingDowngradedSuccessText'));
          } else {
            this.utilityService.showAlert(
              AlertType.success,
              'Success',
              this.translationService.translate(WorkOrderLineEditViolation[response.data?.successCode])
            );
            this.router.navigate(['home/work-orders/work-orders-details', this.workOrder.id]);
          }
          this.blurActiveElement();
          this.saveComplete.emit({ action, success: true, response });
          this.workOrderLineForm.markAsPristine();
        } else if (response && !response.data.success) {
          if (response.data?.validationError.errorCode === 'ERR_MSG_NO_CHANGES_DETECTED') {
            this.utilityService.showAlert(
              AlertType.strong,
              this.translationService.translate('warningText'),
              this.translationService.translate(WorkOrderLineEditViolation[response.data?.validationError.errorCode])
            );
          } else {
            this.utilityService.showAlert(
              AlertType.critical,
              this.translationService.translate('errorText'),
              this.translationService.translate(WorkOrderLineEditViolation[response.data?.validationError.errorCode])
            );
          }
          this.saveComplete.emit({ action, success: false, response });
        }
      },
      error: (err) => {
        this.utilityService.showAlert(
          AlertType.critical,
          'Error',
          this.translationService.translate(WorkOrderLineEditViolation[err?.error.error])
        );
        this.saveComplete.emit({ action, success: false });
      }
    });
  }

  private emitDataChanged(): void {
    const formData = {
      workOrderLine: this.workOrderLineForm.value,
      repairs: this.repairsForm.value
    };
    this.dataChanged.emit(formData);
  }

  private blurActiveElement(): void {
    setTimeout(() => {
      const active = (globalThis as any)?.document?.activeElement as { blur?: () => void } | null;
      if (active?.blur) {
        active.blur();
      }
    });
  }

  // Ensure default for casingValue on blur when cleared or left empty after touch
  ensureCasingValueDefault(): void {
    const ctrl = this.workOrderLineForm.get('casingValue');
    if (!ctrl) return;
    const touchedOrDirty = ctrl.touched || ctrl.dirty;
    const val = String(ctrl.value ?? '').trim();
    if (touchedOrDirty && (!val || val === '.')) {
      ctrl.setValue('0.00', { emitEvent: false });
      ctrl.markAsDirty();
      this.emitDataChanged();
      this.hasChanges.emit(this.workOrderLineForm.dirty || this.repairsForm.dirty);
    }
  }

  onBrandNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = sanitizeBrandNumber(input.value).toUpperCase().slice(0, 25);
    input.value = value;
    this.workOrderLineForm.get('brand')?.setValue(value, { emitEvent: false });
  }

  public getFormData(): any {
    return {
      workOrderLine: this.workOrderLineForm.value,
      repairs: this.repairsForm.value
    };
  }

  public resetForms(): void {
    this.workOrderLineForm.markAsPristine();
    this.repairsForm.markAsPristine();
  }

  setRepairsDataSource(): void {
    this.repairsDataSource = new MatTableDataSource(this.workOrderLineExtended?.repairTable);
  }

  getWorkOrderLineEditableDropdown(): void {
    this.workOrderService.fetchWorkOrderLineInitialLoad(this.dealer.id, this.workOrder.id, this.workOrderLine.id).subscribe((result) => {
      this.prodCustomerOptions = (result.prodCustomerDetails || []).map((item: any) => ({ id: item.id, value: item.customerName }));
      this.prodCustomerOptionsDropdown = (result.prodCustomerDetails || []).map((item: any) => item.customerName);
      this.overrideGradeOptions = (result.overrideGradeDetails || []).map((item: any) => ({
        id: item.casingGradePk,
        value: item.casingGrade
      }));
      this.processCodeOptions = (result.plantProcessCode || []).map((item: any) => ({ value: item.processCode }));
      this.makeOptions = (result.casingMakes || []).map((item: any) => ({
        id: item.casingMakePk,
        value: item.casingMake + ' - ' + item.description
      }));
      this.seriesOptions = (result.casingSeries || []).map((item: any) => ({
        id: item.casingSeriesPk,
        value: item.casingSeries
      }));
      this.sizeOptions = (result.casingSizes || []).map((item: any) => ({
        id: item.id,
        value: item.casingSize
      }));
      this.treadDescOptions = (result.treadDesc || []).map((item: any) => ({
        id: item.id,
        value: item.treadDesc,
        application: item.application,
        axle: item.axle,
        m3Flag: item.m3Flag
      }));
      this.treadLengthOptions = (result.treadLengths || []).map((item: any) => ({
        id: item.id,
        value: item.treadLength
      }));
      this.wheelTypeOptions = (result.wheelReferenceInformation || []).map((item: any) => ({
        id: item.id,
        value: item.referenceValueId
      }));
      this.wheelColorOptions = (result.colorReferenceInformation || []).map((item: any) => ({
        id: item.id,
        value: item.referenceValueId
      }));
      this.rejectReasonsOptions = (result.rejectReasons || []).map((item: any) => ({
        id: item.id,
        code: item.rejectReason,
        value: item.rejectReason + ' - ' + item.rejectDesc
      }));
      this.workOrderLineForm.get('disposalCharge')?.setValue(result.disposalCharge);
    });

    this.createWorkOrderService
      .getApplicationCodes(this.dealer.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((options) => {
        this.applicationOptions = options || [];
      });
    this.createWorkOrderService
      .getAxleCodes(this.dealer.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((options) => {
        this.axleOptions = options || [];
      });
  }

  resetForm() {
    this.populateFormData();
  }

  getIdFromDescription(desc: string, type: string) {
    switch (type) {
      case 'Make':
        return this.makeOptions.find((option) => option.value === desc)?.id || null;
      case 'Series':
        return this.seriesOptions.find((option) => option.value === desc)?.id || null;
      case 'Size':
        return this.sizeOptions.find((option) => option.value === desc)?.id || null;
      case 'TreadDesc':
        return this.treadDescOptions.find((option) => option.value === desc)?.id || null;
      case 'ProdCustomer':
        return this.prodCustomerOptions.find((option) => option.value === desc)?.id || null;
      case 'OverrideGrade':
        return this.overrideGradeOptions.find((option) => option.value === desc)?.id || null;
      case 'WheelType':
        return this.wheelTypeOptions.find((option) => option.value === desc)?.id || null;
      case 'WheelColor':
        return this.wheelColorOptions.find((option) => option.value === desc)?.id || null;
      case 'RejectCode':
        return this.rejectReasonsOptions.find((option) => option.value === desc)?.id || null;
      case 'TreadLength':
        return this.treadLengthOptions.find((option) => option.value === desc)?.id || null;
      default:
        return null;
    }
  }

  // Helper function to extract id from value or lookup
  getId = (value, options) => {
    if (!value) return null;
    if (typeof value === 'object' && value.id) return value.id;
    const found = options.find((opt) => opt.value === value);
    return found ? found.id : value;
  };

  setRequiredBasedOnFlag = () => {
    this.workOrderRequiredFieldFlag = this.store.selectSnapshot(DealerState.workOrderRequiredFieldFlags);
    const workType = this.workOrderLineForm.get('workType')?.value;
    if (this.workOrderRequiredFieldFlag?.ntrRequired === REQUIRED && !this.disableNTRValidation.includes(workType)) {
      this.workOrderLineForm.get('ntr').setValidators([Validators.required]);
      this.workOrderLineForm.get('ntr').updateValueAndValidity({ emitEvent: false });
    }
    if (this.workOrderRequiredFieldFlag?.treadRequired === REQUIRED && !this.specialWorkType.includes(workType)) {
      this.workOrderLineForm.get('treadDesc').setValidators([Validators.required]);
      this.workOrderLineForm.get('treadDesc').updateValueAndValidity({ emitEvent: false });
    }

    if (this.workOrderEditable) {
      this.workOrderLineForm.get('length')?.updateValueAndValidity({ emitEvent: false });
    }
  };

  formatNumber(casingValueCtrl: AbstractControl) {
    let nStr = (casingValueCtrl.value ?? '') + '';
    let x = nStr.split('.');
    let x1 = '';
    let x2 = '';
    if (nStr === '') {
      casingValueCtrl.setValue('0.00', { emitEvent: false });
    } else {
      if (x.length > 1) {
        x1 = x[0];
        x2 = '.' + x[1].substring(0, 2);
        for (let i = 0; i < 2 - x[1].length; i++) {
          x2 = x2 + '0';
        }
        casingValueCtrl.setValue(x1 + x2, { emitEvent: false });
      } else {
        if (x[0].length > 6) {
          x1 = x[0].substring(0, 6);
          x2 = '.' + x[0].slice(3, 3 + x[0].length);
          casingValueCtrl.setValue(x1 + x2, { emitEvent: false });
          this.formatNumber(casingValueCtrl);
        } else {
          casingValueCtrl.setValue(x[0] + '.00', { emitEvent: false });
        }
      }
    }
  }

  onMoldTypeChange(): void {
    const currentMake = this.workOrderLineForm.get('make').value;
    const currentSeries = this.workOrderLineForm.get('series').value;
    const currentSize = this.workOrderLineForm.get('size').value;
    this.refreshTreadsDescDropdown(currentMake, currentSeries, currentSize);
  }

  onChangeTreadDescRequired(workType: string): void {
    const treadDescControl = this.workOrderLineForm.get('treadDesc');
    if (this.workOrderRequiredFieldFlag?.treadRequired === REQUIRED && !this.specialWorkType.includes(workType)) {
      treadDescControl.setValidators([Validators.required]);
      treadDescControl.updateValueAndValidity({ emitEvent: false });
    } else {
      treadDescControl.clearValidators();
      treadDescControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  getM3Flag(treadDescValue: any): boolean {
    const selectedTread = this.treadDescOptions.find((opt) => opt.value === treadDescValue);
    return selectedTread?.m3Flag === 'Y';
  }

  refreshTreadsDescDropdown(make, series, size): void {
    this.workOrderService
      .refreshTreadDropdown(
        this.dealer.id,
        this.workOrderLine.id,
        make?.id != undefined ? make.id : this.getIdFromDescription(make, 'Make'),
        series?.id != undefined ? series.id : this.getIdFromDescription(series, 'Series'),
        size?.id != undefined ? size.id : this.getIdFromDescription(size, 'Size'),
        this.workOrderLineForm.get('moldType')?.value
      )
      .subscribe((treads: any) => {
        this.treadDescOptions = (treads || []).map((item: any) => ({
          id: item.id,
          value: item.treadDesc,
          application: item.application,
          axle: item.axle,
          m3Flag: item.m3Flag
        }));

        // Reselect the tread if it exists in the new options
        const currentTread =
          this.workOrderLineForm.get('treadDesc')?.value?.value != undefined
            ? this.workOrderLineForm.get('treadDesc')?.value?.value
            : this.workOrderLineForm.get('treadDesc')?.value;
        const matchedTread = this.treadDescOptions.find((opt) => {
          return opt.value == currentTread;
        });
        if (matchedTread && !this.treadDescReadOnly) {
          this.workOrderLineForm.get('treadDesc')?.setValue(matchedTread, { emitEvent: false });
        } else {
          this.workOrderLineForm.get('treadDesc')?.setValue(null, { emitEvent: false });
        }
      });
  }
}
